/**
 * DAO Service - BUILD SAFE VERSION
 * 
 * This is a mock implementation of the DAO service to avoid build errors.
 * It provides the same API but returns mock data.
 */

// Mock data for the DAO service
const mockProposals = [
  {
    id: '1',
    title: 'Increase Staking Rewards',
    description: 'Proposal to increase the staking rewards by 20% for all Angels Key holders.',
    status: 'active',
    createdAt: '2025-04-15T10:30:00Z',
    votes: { yes: 120, no: 30, abstain: 5 },
    creator: '0x1234...5678'
  },
  {
    id: '2',
    title: 'Add New Game Rewards',
    description: 'Add new reward tiers for the scratch card games.',
    status: 'completed',
    createdAt: '2025-04-10T14:20:00Z',
    votes: { yes: 150, no: 10, abstain: 2 },
    creator: '0xabcd...efgh'
  }
];

const mockTreasuryData = {
  totalValue: 1500000,
  pools: [
    { name: 'Development', allocation: 0.4, value: 600000 },
    { name: 'Marketing', allocation: 0.3, value: 450000 },
    { name: 'Community', allocation: 0.2, value: 300000 },
    { name: 'Reserve', allocation: 0.1, value: 150000 }
  ]
};

class DAOService {
  constructor() {
    this.baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
    this.apiUrl = `${this.baseUrl}/api/dao`;
  }

  /**
   * Get all proposals
   * @param {Object} options - Query options
   * @param {string} options.status - Filter by status
   * @param {number} options.limit - Number of proposals to retrieve
   * @param {number} options.skip - Number of proposals to skip
   * @returns {Promise<Array>} - Array of proposals
   */
  async getProposals(options = {}) {
    const { status } = options;
    
    // Filter by status if provided
    let filteredProposals = mockProposals;
    if (status) {
      filteredProposals = mockProposals.filter(p => p.status === status);
    }
    
    return Promise.resolve(filteredProposals);
  }

  /**
   * Get a specific proposal by ID
   * @param {string} proposalId - ID of the proposal to retrieve
   * @returns {Promise<Object>} - Proposal details
   */
  async getProposalById(proposalId) {
    const proposal = mockProposals.find(p => p.id === proposalId);
    
    if (!proposal) {
      return Promise.reject(new Error(`Proposal with ID ${proposalId} not found`));
    }
    
    return Promise.resolve(proposal);
  }

  /**
   * Create a new proposal
   * @param {Object} proposalData - Proposal data
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Created proposal
   */
  async createProposal(proposalData, token) {
    // Create a new mock proposal with the provided data
    const newProposal = {
      id: `${mockProposals.length + 1}`,
      ...proposalData,
      status: 'active',
      createdAt: new Date().toISOString(),
      votes: { yes: 0, no: 0, abstain: 0 },
      creator: '0xmock...address'
    };
    
    return Promise.resolve(newProposal);
  }

  /**
   * Vote on a proposal
   * @param {string} proposalId - ID of the proposal to vote on
   * @param {Object} voteData - Vote data
   * @param {boolean} voteData.inFavor - Whether the vote is in favor
   * @param {number} voteData.amount - Amount of voting power to use
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Vote record
   */
  async voteOnProposal(proposalId, voteData, token) {
    // Create a mock vote record
    const voteRecord = {
      id: `vote-${Date.now()}`,
      proposalId,
      vote: voteData.inFavor ? 'yes' : 'no',
      amount: voteData.amount,
      timestamp: new Date().toISOString(),
      voter: '0xmock...voter'
    };
    
    return Promise.resolve(voteRecord);
  }

  /**
   * Execute a proposal (admin only)
   * @param {string} proposalId - ID of the proposal to execute
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Updated proposal
   */
  async executeProposal(proposalId, token) {
    // Find the proposal and update its status
    const proposal = mockProposals.find(p => p.id === proposalId);
    
    if (!proposal) {
      return Promise.reject(new Error(`Proposal with ID ${proposalId} not found`));
    }
    
    const updatedProposal = {
      ...proposal,
      status: 'executed',
      executedAt: new Date().toISOString()
    };
    
    return Promise.resolve(updatedProposal);
  }

  /**
   * Cancel a proposal (admin or creator only)
   * @param {string} proposalId - ID of the proposal to cancel
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - Updated proposal
   */
  async cancelProposal(proposalId, token) {
    // Find the proposal and update its status
    const proposal = mockProposals.find(p => p.id === proposalId);
    
    if (!proposal) {
      return Promise.reject(new Error(`Proposal with ID ${proposalId} not found`));
    }
    
    const updatedProposal = {
      ...proposal,
      status: 'cancelled',
      cancelledAt: new Date().toISOString()
    };
    
    return Promise.resolve(updatedProposal);
  }

  /**
   * Get user's governance tokens
   * @param {string} token - Authentication token
   * @returns {Promise<Object>} - User's governance tokens
   */
  async getUserTokens(token) {
    // Return mock governance tokens
    const mockTokens = {
      totalTokens: 1000,
      votingPower: 850,
      delegatedPower: 150,
      delegatedTo: null,
      tokenHistory: [
        { date: '2025-04-01', amount: 1000, type: 'mint' }
      ]
    };
    
    return Promise.resolve(mockTokens);
  }

  /**
   * Get user's votes
   * @param {string} token - Authentication token
   * @returns {Promise<Array>} - Array of user's votes
   */
  async getUserVotes(token) {
    // Return mock user votes
    const mockVotes = [
      {
        id: 'vote-1',
        proposalId: '1',
        vote: 'yes',
        amount: 100,
        timestamp: '2025-04-15T12:30:00Z',
        voter: '0xmock...voter'
      },
      {
        id: 'vote-2',
        proposalId: '2',
        vote: 'no',
        amount: 50,
        timestamp: '2025-04-10T15:45:00Z',
        voter: '0xmock...voter'
      }
    ];
    
    return Promise.resolve(mockVotes);
  }

  /**
   * Get votes for a proposal
   * @param {string} proposalId - ID of the proposal
   * @param {Object} options - Query options
   * @param {number} options.limit - Number of votes to retrieve
   * @param {number} options.skip - Number of votes to skip
   * @returns {Promise<Array>} - Array of votes for the proposal
   */
  async getProposalVotes(proposalId, options = {}) {
    // Return mock votes for the proposal
    const mockVotes = [
      {
        id: `vote-${proposalId}-1`,
        proposalId,
        vote: 'yes',
        amount: 120,
        timestamp: '2025-04-15T10:35:00Z',
        voter: '0xvoter1...address'
      },
      {
        id: `vote-${proposalId}-2`,
        proposalId,
        vote: 'no',
        amount: 30,
        timestamp: '2025-04-15T11:20:00Z',
        voter: '0xvoter2...address'
      },
      {
        id: `vote-${proposalId}-3`,
        proposalId,
        vote: 'abstain',
        amount: 5,
        timestamp: '2025-04-15T12:15:00Z',
        voter: '0xvoter3...address'
      }
    ];
    
    return Promise.resolve(mockVotes);
  }
}

// Create a singleton instance
const daoService = new DAOService();
export default daoService;
